import type { EditorAPI } from '#packages/editorAPI';
import type { StageRect } from '#packages/stateManagement';
import type { CompLayout, CompRef } from '@wix/document-services-types';
import type {
  CollisionCallback,
  ContainerData,
  RestrictedZone,
} from '../plugin/types';

export function adjustLayoutToFit(
  containerRect: StageRect,
  layout: CompLayout,
  onCollision: CollisionCallback,
): CompLayout {
  let adjustedLayout = { ...layout };

  adjustedLayout = adjustLayoutHorizontally(
    containerRect,
    adjustedLayout,
    onCollision,
  );
  adjustedLayout = adjustLayoutVertically(
    containerRect,
    adjustedLayout,
    onCollision,
  );

  return adjustedLayout;
}

export function adjustLayoutToFitSection(
  containerRect: StageRect,
  layout: CompLayout,
  onCollision: CollisionCallback,
): CompLayout {
  return adjustLayoutVertically(containerRect, layout, onCollision);
}

export function adjustLayoutHorizontally(
  containerRect: StageRect,
  layout: CompLayout,
  onCollision: CollisionCallback,
): CompLayout {
  const adjustedLayout = { ...layout };

  if (layout.x < containerRect.left) {
    adjustedLayout.x = containerRect.left;
    adjustedLayout.bounding.x = containerRect.left;

    onCollision('horizontal', 'left');
  } else if (layout.x + layout.width > containerRect.right) {
    const adjustedX = containerRect.right - layout.width;
    adjustedLayout.x = adjustedX;
    adjustedLayout.bounding.x = adjustedX;

    onCollision('horizontal', 'right');
  }

  return adjustedLayout;
}

export function adjustLayoutVertically(
  containerRect: StageRect,
  layout: CompLayout,
  onCollision: CollisionCallback,
): CompLayout {
  const adjustedLayout = { ...layout };

  if (layout.y < containerRect.top) {
    adjustedLayout.y = containerRect.top;
    adjustedLayout.bounding.y = containerRect.top;

    onCollision('vertical', 'top');
  } else if (layout.y + layout.height > containerRect.bottom) {
    const adjustedY = containerRect.bottom - layout.height;
    adjustedLayout.y = adjustedY;
    adjustedLayout.bounding.y = adjustedY;

    onCollision('vertical', 'bottom');
  }

  return adjustedLayout;
}

export function getIsOverlap(rect1: CompLayout, rect2: CompLayout): boolean {
  const isHorizontalOverlap = (a: CompLayout, b: CompLayout) =>
    a.x < b.x + b.width && a.x + a.width > b.x;

  const isVerticalOverlap = (a: CompLayout, b: CompLayout) =>
    a.y < b.y + b.height && a.y + a.height > b.y;

  return isHorizontalOverlap(rect1, rect2) && isVerticalOverlap(rect1, rect2);
}

export const areAllComponentsMobileOnly = (
  editorAPI: EditorAPI,
  selectedComp: CompRef[],
): boolean => {
  const isMobileOnlyComponent =
    editorAPI.mobile.mobileOnlyComponents.isMobileOnlyComponent;

  return selectedComp.every((comp) => isMobileOnlyComponent(comp.id));
};

export function getOverlappedSiblings(
  editorAPI: EditorAPI,
  selectedComp: CompRef[],
  siblingContainers: ContainerData[],
): RestrictedZone[] {
  const layoutGet =
    editorAPI.components.layout.measure.getBoundingLayoutRelativeToScreen;

  const selectedRects = selectedComp.map((comp) => layoutGet(comp));

  return siblingContainers
    .map((sibl) => {
      const siblRect = layoutGet(sibl.ref);

      const isOverlapping = selectedRects.some((compRect) =>
        getIsOverlap(compRect, siblRect),
      );

      return isOverlapping ? { ...siblRect, compRef: sibl.ref } : null;
    })
    .filter(Boolean);
}

export function getDraggedLayout(
  editorAPI: EditorAPI,
  selectedComp: CompRef[],
) {
  return selectedComp.map((comp) => {
    const rotationInDegrees =
      editorAPI.components.layout.get_rotationInDegrees(comp);
    const componentRect =
      editorAPI.components.layout.getRelativeToScreen_rect(comp);

    return {
      ...componentRect,
      rotationInDegrees,
    };
  });
}

export function getComponentRect(
  editorAPI: EditorAPI,
  containerRef: CompRef,
): StageRect {
  const containerRect =
    editorAPI.components.layout.getRelativeToScreen_rect(containerRef);

  return {
    top: containerRect.y,
    bottom: containerRect.y + containerRect.height,
    left: containerRect.x,
    right: containerRect.x + containerRect.width,
  };
}

function getContainerData(
  editorAPI: EditorAPI,
  containerRef: CompRef,
): ContainerData {
  const rect = getComponentRect(editorAPI, containerRef);
  const compType = editorAPI.components.getType(containerRef);

  return { ref: containerRef, rect, compType };
}

export function getParentContainers(
  editorAPI: EditorAPI,
  selectedComponents: CompRef[],
): ContainerData[] {
  return selectedComponents.map((selectedComp) =>
    getContainerData(
      editorAPI,
      editorAPI.components.getContainer(selectedComp),
    ),
  );
}

export function getSiblingContainers(
  editorAPI: EditorAPI,
  selectedComponents: CompRef[],
): ContainerData[] {
  return selectedComponents
    .map((comp) => editorAPI.components.getSiblings(comp))
    .flat()
    .filter((sibling) => editorAPI.components.is.container(sibling))
    .map((containerRef) => getContainerData(editorAPI, containerRef));
}
